import { useCallback, useEffect, useRef, useState } from 'react';
import { api, type AnalysisResponse, type Game } from './api';

type Kind = 'prediction' | 'betting' | 'narrative' | 'causation';
const cache = new Map<string, AnalysisResponse>();
const key = (g: Game, kind: Kind) => `${g.id}:${kind}`;

/** Run one analysis per game per kind, cached for the session so tabs and pages never re-pay for a model call. */
export function useAnalysis(game: Game | undefined, kind: Kind, autorun = false) {
  const [state, setState] = useState<{ data?: AnalysisResponse; loading: boolean; error?: string }>(() => ({
    data: game ? cache.get(key(game, kind)) : undefined, loading: false,
  }));

  const run = useCallback(async () => {
    if (!game) return;
    const hit = cache.get(key(game, kind));
    if (hit) { setState({ data: hit, loading: false }); return; }
    setState({ loading: true });
    const home = game.home_team.name, away = game.away_team.name;
    try {
      const data = kind === 'betting' ? await api.bettingCoach(game)
        : kind === 'narrative' ? await api.narratives(home, away, game.league ?? 'nfl')
        : kind === 'causation' ? await api.why(game.id, `Why did ${away} at ${home} turn out the way it did?`, game.league ?? 'nfl')
        : await api.analyze(`Who wins ${away} at ${home} and why?`, { home_team: home, away_team: away, intent: 'prediction', sport: game.league ?? 'nfl' });
      cache.set(key(game, kind), data);
      setState({ data, loading: false });
    } catch (e) {
      setState({ loading: false, error: (e as Error).message });
    }
  }, [game, kind]);

  // when the visitor arrived from a tool page, start straight away instead of showing the launch card
  const started = useRef(false);
  useEffect(() => {
    if (autorun && game && !started.current && !state.data && !state.loading) { started.current = true; void run(); }
  }, [autorun, game, run, state.data, state.loading]);

  return { ...state, run };
}
